import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Play } from "lucide-react";
import { toast } from "sonner";

function FieldRow({ label, ok }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-900 py-2">
      <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">{label}</span>
      {ok ? (
        <span className="inline-flex items-center gap-1 text-emerald-400 font-mono text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> DETECTED</span>
      ) : (
        <span className="inline-flex items-center gap-1 text-red-400 font-mono text-xs"><XCircle className="h-3.5 w-3.5" /> MISSING</span>
      )}
    </div>
  );
}

export default function Tester() {
  const [url, setUrl] = useState("");
  const [ua, setUa] = useState("");
  const [headers, setHeaders] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    let parsedHeaders = null;
    if (headers.trim()) {
      try { parsedHeaders = JSON.parse(headers); }
      catch { toast.error("Headers must be valid JSON"); return; }
    }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/test-url", {
        url,
        user_agent: ua || null,
        custom_headers: parsedHeaders,
      });
      setResult(data);
      if (data.status === "ok") toast.success(`OK — ${data.items_count} items`);
      else toast.error(`Failed — ${data.error || data.http_status}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="tester-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Diagnostics</div>
        <h1 className="text-2xl md:text-3xl font-mono font-semibold tracking-tight">URL Tester</h1>
        <p className="text-sm text-zinc-400 mt-1">Probe a feed URL and inspect detected fields before adding.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Left: form + diagnostics */}
        <div className="space-y-4">
          <form onSubmit={submit} className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-5 space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Feed URL</Label>
              <Input data-testid="tester-url-input" value={url} onChange={(e) => setUrl(e.target.value)} required className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono" placeholder="https://example.com/rss" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">User-Agent (optional)</Label>
              <Input value={ua} onChange={(e) => setUa(e.target.value)} className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono text-xs" placeholder="Mozilla/5.0 …" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Custom Headers (JSON, optional)</Label>
              <Textarea rows={3} value={headers} onChange={(e) => setHeaders(e.target.value)} className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono text-xs" placeholder='{ "Referer": "https://example.com" }' />
            </div>
            <Button data-testid="tester-run-btn" type="submit" disabled={busy} className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono gap-2 w-full">
              <Play className="h-4 w-4" /> {busy ? "TESTING…" : "RUN TEST"}
            </Button>
          </form>

          {result && (
            <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-5" data-testid="tester-diagnostics">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Diagnostics</div>
                {result.status === "ok" ? (
                  <Badge className="rounded-sm bg-transparent border border-emerald-500/60 text-emerald-400 font-mono uppercase tracking-widest">USABLE</Badge>
                ) : (
                  <Badge className="rounded-sm bg-transparent border border-red-500/60 text-red-400 font-mono uppercase tracking-widest">FAILED</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono mb-3">
                <span className="text-zinc-500">http_status</span><span className="text-white text-right">{result.http_status ?? "—"}</span>
                <span className="text-zinc-500">feed_title</span><span className="text-white text-right truncate">{result.feed_title || "—"}</span>
                <span className="text-zinc-500">items_count</span><span className="text-white text-right">{result.items_count}</span>
                {result.error && (<><span className="text-zinc-500">error</span><span className="text-red-400 text-right truncate" title={result.error}>{result.error}</span></>)}
              </div>

              {/* Freshness block */}
              {result.freshness && (
                <div className="border-t border-zinc-800 pt-3 mb-3" data-testid="freshness-block">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Freshness</span>
                    {result.freshness.label === "fresh" && <Badge className="rounded-sm bg-transparent border border-emerald-500/60 text-emerald-400 font-mono uppercase tracking-widest text-[10px]">FRESH</Badge>}
                    {result.freshness.label === "stale" && <Badge className="rounded-sm bg-transparent border border-amber-500/60 text-amber-400 font-mono uppercase tracking-widest text-[10px]">STALE</Badge>}
                    {result.freshness.label === "dead" && <Badge className="rounded-sm bg-transparent border border-red-500/60 text-red-400 font-mono uppercase tracking-widest text-[10px]">DEAD (&gt;30d)</Badge>}
                    {result.freshness.label === "unknown" && <Badge className="rounded-sm bg-transparent border border-zinc-700 text-zinc-400 font-mono uppercase tracking-widest text-[10px]">NO DATES</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono">
                    <span className="text-zinc-500">newest_item</span>
                    <span className="text-white text-right">
                      {result.freshness.newest_age_hours != null
                        ? (result.freshness.newest_age_hours < 24
                            ? `${result.freshness.newest_age_hours}h ago`
                            : `${Math.round(result.freshness.newest_age_hours / 24)}d ago`)
                        : "—"}
                    </span>
                    <span className="text-zinc-500">dated_items</span>
                    <span className="text-white text-right">{result.freshness.dated_items} / {result.items_count}</span>
                  </div>
                </div>
              )}
              <div className="space-y-0 border-t border-zinc-800 pt-2">
                <FieldRow label="title" ok={result.detected_fields?.title} />
                <FieldRow label="link" ok={result.detected_fields?.link} />
                <FieldRow label="summary/description" ok={result.detected_fields?.summary} />
                <FieldRow label="date" ok={result.detected_fields?.date} />
                <FieldRow label="author" ok={result.detected_fields?.author} />
              </div>
            </div>
          )}
        </div>

        {/* Right: preview */}
        <div className="border border-zinc-800 bg-[#050505] rounded-sm min-h-[400px]" data-testid="tester-preview">
          <div className="px-4 py-2 border-b border-zinc-800 flex items-center justify-between">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Sample Items (first 10)</div>
            <span className="blink text-emerald-400 font-mono text-[10px]">■</span>
          </div>
          <div className="p-4 max-h-[560px] overflow-y-auto space-y-3">
            {!result && <div className="text-zinc-600 font-mono text-xs">stdout empty — run test to inspect items…</div>}
            {result?.sample_items?.length === 0 && <div className="text-zinc-500 font-mono text-xs">no items returned</div>}
            {result?.sample_items?.map((it, i) => (
              <div key={i} className="border-l-2 border-zinc-800 pl-3">
                <div className="text-[10px] font-mono text-zinc-500">[{i + 1}] {it.published_at || "no-date"}</div>
                <div className="text-sm text-white mt-0.5 font-mono truncate">{it.title || "(no title)"}</div>
                {it.link && <div className="text-[11px] text-zinc-500 font-mono truncate">{it.link}</div>}
                {it.summary && <div className="text-xs text-zinc-400 mt-1 line-clamp-2" dangerouslySetInnerHTML={{ __html: it.summary }} />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
